//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { Select } from "@chakra-ui/react";
import React from "react";
import { useFetchOrganizationDeployments } from "../../newDeployment/useFetchOrganizationDeployments";

// ALL_DEPLOYMENTS is the sentinel option value meaning "no deployment
// filter" - the org-level chart shows every deployment, stacked. It is not a
// valid deployment id, so it can never collide with a real one.
export const ALL_DEPLOYMENTS = "";

// DeploymentUsageFilter lets the viewer narrow the organization-level usage
// chart down to a single deployment. Deployments with no usage in the
// selected range are still listed - this is a deployment picker, not a
// filtered view of what happened to show up in the chart already, so
// switching range should not make an option disappear out from under the
// viewer.
export const DeploymentUsageFilter = ({ value, onChange }: { value: string; onChange: (deploymentId: string) => void }) => {
  // pageNumber 0 / pageSize 500: matches useResolveDeploymentNames' own call
  // to this same hook elsewhere on this page exactly, so react-query treats
  // them as the same query (one request/cache entry, not two) - see that
  // hook's comment. 500: organizations run nowhere near that many
  // deployments in practice.
  const { data, isLoading } = useFetchOrganizationDeployments({ pageNumber: 0, pageSize: 500 });
  const deployments = data?.items || [];

  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      isDisabled={isLoading}
      width="auto"
      minWidth="220px"
      aria-label="Filter usage by deployment"
    >
      <option value={ALL_DEPLOYMENTS}>All deployments</option>
      {deployments.map((deployment) => (
        <option key={deployment.id} value={deployment.id}>
          {deployment.name || deployment.id}
        </option>
      ))}
    </Select>
  );
};
